// V2ProjectsPage — first-class Projects (work-tracking, not billing).
// Full CRUD + stage menu portal + Emergent/Claude deep-link management.
// Cascade + user-restore-pin discipline is enforced server-side.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Chrome, C } from './V2AdminOSPage';

const req = async (method, path, body) => {
  const url = (process.env.REACT_APP_BACKEND_URL || '') + path;
  const token = localStorage.getItem('kindra-admin-token');
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const text = await res.text();
  const parsed = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(parsed?.detail || parsed?.message || res.statusText);
  return parsed;
};

const STAGES = ['idea', 'active', 'paused', 'delivered', 'maintained'];
const STAGE_LABEL = { idea: 'Idea', active: 'Active', paused: 'Paused', delivered: 'Delivered', maintained: 'Maintained' };
const STAGE_C = { idea: C.faint, active: '#34D1B0', paused: '#F0C36D', delivered: '#4F8FD0', maintained: '#9156C4' };

const hFont = { fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 700, color: C.icy };
const card = { background: C.surface, border: `1px solid ${C.hair}`, borderRadius: 14, padding: 16 };
const ghost = { background: 'transparent', border: `1px solid ${C.hair}`, color: C.icy, padding: '7px 14px', borderRadius: 999, cursor: 'pointer', fontFamily: 'Inter', fontSize: 12, fontWeight: 600 };
const btnPrimary = { background: '#F0C36D', color: '#0B0526', border: 'none', padding: '9px 18px', borderRadius: 999, cursor: 'pointer', fontFamily: 'Inter', fontSize: 13, fontWeight: 800 };
const eyebrow = { display: 'inline-block', fontFamily: 'Inter', fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginBottom: 4 };
const chip = (c) => ({ background: `${c}22`, color: c, padding: '3px 10px', borderRadius: 999, fontFamily: 'Inter', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'inline-block' });
const inp = { width: '100%', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.hair}`, borderRadius: 10, padding: '10px 12px', color: C.icy, fontFamily: 'Inter', fontSize: 13 };

// ── Stage menu portal (same pattern as quote status menu) ─────────────
const StageMenu = ({ current, anchorRef, onPick, onClose }) => {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!anchorRef?.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left });
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    const scroll = () => onClose();
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', scroll);
    window.addEventListener('scroll', scroll, true);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('resize', scroll); window.removeEventListener('scroll', scroll, true); };
  }, [anchorRef, onClose]);
  if (!pos) return null;
  return createPortal(
    <div ref={menuRef} data-testid="proj-stage-menu"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 5000, background: C.surface, border: `1px solid ${C.hairBright}`, borderRadius: 12, padding: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.6)', minWidth: 180 }}>
      <p style={{ margin: '4px 8px 6px', ...eyebrow, marginBottom: 6 }}>Move to stage</p>
      {STAGES.map((s) => {
        const cur = s === current;
        return (
          <button key={s} data-testid={`proj-stage-pick-${s}`} onClick={(e) => { e.stopPropagation(); if (!cur) onPick(s); }} disabled={cur}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 10px', background: cur ? 'rgba(255,255,255,0.05)' : 'transparent', border: 'none', borderRadius: 8, cursor: cur ? 'default' : 'pointer', color: cur ? C.icy : STAGE_C[s] || C.icy, fontFamily: 'Inter', fontSize: 12, fontWeight: 700, textAlign: 'left' }}
            onMouseEnter={(e) => { if (!cur) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            onMouseLeave={(e) => { if (!cur) e.currentTarget.style.background = 'transparent'; }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: STAGE_C[s] }} />
            <span style={{ flex: 1 }}>{STAGE_LABEL[s]}</span>
            {cur && <span style={{ color: C.faint, fontSize: 10 }}>current</span>}
          </button>
        );
      })}
    </div>,
    document.body,
  );
};

const ProjectRow = ({ p, onOpen, onArchive, onRestore, onDelete }) => (
  <div key={p.project_id} data-testid={`proj-row-${p.project_id}`} onClick={() => onOpen(p.project_id)}
    style={{ ...card, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', borderLeft: `3px solid ${STAGE_C[p.stage] || C.hair}` }}>
    <div style={{ flex: 1, minWidth: 200 }}>
      <p style={{ ...hFont, margin: 0, fontSize: 15 }}>{p.name}</p>
      <p style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 11.5, color: C.muted }}>
        {p.value ? <b style={{ color: C.icy }}>${p.value.toLocaleString()}</b> : null}
        {p.value ? ' · ' : ''}
        {(p.emergent_links || []).length > 0 && <span style={{ color: '#34D1B0' }}>◈ {(p.emergent_links || []).length} Emergent link{(p.emergent_links || []).length !== 1 ? 's' : ''}</span>}
        {(p.claude_links || []).length > 0 && <span style={{ color: '#F0C36D' }}> · ⧉ {(p.claude_links || []).length} Claude thread{(p.claude_links || []).length !== 1 ? 's' : ''}</span>}
      </p>
    </div>
    <span style={chip(STAGE_C[p.stage] || C.faint)}>{STAGE_LABEL[p.stage] || p.stage}</span>
    {!p.archived
      ? <button data-testid={`proj-row-archive-${p.project_id}`} onClick={(e) => { e.stopPropagation(); onArchive(p); }} style={{ ...ghost, padding: '5px 12px', fontSize: 11 }}>🗄 Archive</button>
      : <button data-testid={`proj-row-restore-${p.project_id}`} onClick={(e) => { e.stopPropagation(); onRestore(p); }} style={{ ...ghost, padding: '5px 12px', fontSize: 11, color: '#34D1B0', borderColor: 'rgba(52,209,176,0.4)' }}>↺ Restore</button>}
    {p.archived && <button data-testid={`proj-row-delete-${p.project_id}`} onClick={(e) => { e.stopPropagation(); onDelete(p); }} style={{ ...ghost, padding: '5px 12px', fontSize: 11, color: '#FF8FC1', borderColor: 'rgba(235,37,141,0.4)' }}>🗑 Delete</button>}
  </div>
);

// ── List view ─────────────────────────────────────────────────────────
const ProjectList = ({ projects, showArchived, setShowArchived, onOpen, onNew, onArchive, onRestore, onDelete }) => {
  const grouped = { internal: [], client: {} };
  (projects || []).forEach((p) => {
    if (!p.client_ref) grouped.internal.push(p);
    else {
      const k = p.client_tag || p.client_ref;
      (grouped.client[k] = grouped.client[k] || []).push(p);
    }
  });
  const rowProps = { onOpen, onArchive, onRestore, onDelete };
  return (
    <div data-testid="projects-list">
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 26, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <span style={eyebrow}>Work</span>
          <h1 style={{ ...hFont, margin: 0, fontSize: 30 }}>Projects{showArchived ? ' — Archive' : ''}</h1>
        </div>
        <button data-testid="proj-toggle-archive" onClick={() => setShowArchived(!showArchived)} style={ghost}>{showArchived ? '← Active' : '🗄 Archive'}</button>
        {!showArchived && <button data-testid="proj-new-btn" onClick={onNew} style={btnPrimary}>+ New project</button>}
      </div>
      {projects && projects.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: 48 }}>
          <p style={{ margin: 0, fontFamily: 'Inter', color: C.muted, fontSize: 14 }}>{showArchived ? 'Archive is empty — nothing archived yet.' : 'No projects yet — hit “+ New project” to start tracking a build.'}</p>
        </div>
      )}
      {!showArchived && grouped.internal.length > 0 && (
        <>
          <p style={{ ...eyebrow, marginTop: 20, marginBottom: 10, fontSize: 11 }}>Internal · TravelTrucks</p>
          <div style={{ display: 'grid', gap: 10 }}>{grouped.internal.map((p) => <ProjectRow key={p.project_id} p={p} {...rowProps} />)}</div>
        </>
      )}
      {!showArchived && Object.keys(grouped.client).sort().map((k) => (
        <div key={k}>
          <p style={{ ...eyebrow, marginTop: 22, marginBottom: 10, fontSize: 11 }}>{k}</p>
          <div style={{ display: 'grid', gap: 10 }}>{grouped.client[k].map((p) => <ProjectRow key={p.project_id} p={p} {...rowProps} />)}</div>
        </div>
      ))}
      {showArchived && (
        <div style={{ display: 'grid', gap: 10 }}>{(projects || []).map((p) => <ProjectRow key={p.project_id} p={p} {...rowProps} />)}</div>
      )}
    </div>
  );
};

// ── Composer / Detail — single form component for create + edit ────────
const Composer = ({ project, customers, say, onBack, onSaved }) => {
  const isEdit = !!project;
  const [name, setName] = useState(project?.name || '');
  const [stage, setStage] = useState(project?.stage || 'idea');
  const [clientRef, setClientRef] = useState(project?.client_ref || '');
  const [value, setValue] = useState(project?.value ?? '');
  const [manualSpend, setManualSpend] = useState(project?.manual_spend ?? '');
  const [notes, setNotes] = useState(project?.notes || '');
  const [eLinks, setELinks] = useState(project?.emergent_links || []);
  const [cLinks, setCLinks] = useState(project?.claude_links || []);
  const [busy, setBusy] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const stageRef = useRef(null);

  const addLink = (kind, url, label) => {
    if (!url) return;
    const link = { link_id: Math.random().toString(36).slice(2, 12), kind, url, label: label || null, added_at: new Date().toISOString() };
    if (kind === 'emergent') setELinks([...eLinks, link]); else setCLinks([...cLinks, link]);
  };
  const rmLink = (kind, link_id) => {
    if (kind === 'emergent') setELinks(eLinks.filter((l) => l.link_id !== link_id));
    else setCLinks(cLinks.filter((l) => l.link_id !== link_id));
  };

  const save = async () => {
    if (!name.trim()) { say('Give the project a name.'); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(), stage, client_ref: clientRef || null,
        value: value === '' ? null : Number(value),
        manual_spend: manualSpend === '' ? null : Number(manualSpend),
        notes, emergent_links: eLinks, claude_links: cLinks,
      };
      const res = isEdit
        ? await req('PATCH', `/api/admin/projects/${project.project_id}`, payload)
        : await req('POST', '/api/admin/projects', payload);
      say(`✓ ${res.project.name} saved`);
      onSaved(res.project);
    } catch (e) { say('Save failed: ' + (e.message || 'unknown')); }
    setBusy(false);
  };

  const pickStage = async (s) => {
    setStage(s);
    setStageOpen(false);
    if (isEdit) {
      try { await req('PATCH', `/api/admin/projects/${project.project_id}`, { stage: s }); say(`Stage → ${STAGE_LABEL[s]}`); }
      catch (e) { say('Stage change failed'); }
    }
  };

  return (
    <div data-testid={isEdit ? 'proj-detail' : 'proj-composer'} style={{ display: 'grid', gap: 20 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <button data-testid="proj-back" onClick={onBack} style={ghost}>← projects</button>
        <div style={{ flex: 1 }}>
          <span style={eyebrow}>{isEdit ? 'Project' : 'New project'}</span>
          <h1 style={{ ...hFont, margin: 0, fontSize: 26 }}>{isEdit ? project.name : 'New project'}</h1>
        </div>
        {isEdit && (
          <>
            <button ref={stageRef} data-testid="proj-stage-chip" onClick={() => setStageOpen(!stageOpen)}
              style={{ ...chip(STAGE_C[stage]), padding: '6px 14px', fontSize: 11, cursor: 'pointer', border: 'none', background: `${STAGE_C[stage]}22` }}>
              {STAGE_LABEL[stage]} <span style={{ opacity: 0.55, fontSize: 9, marginLeft: 3 }}>▾</span>
            </button>
            {stageOpen && <StageMenu current={stage} anchorRef={stageRef} onPick={pickStage} onClose={() => setStageOpen(false)} />}
          </>
        )}
      </div>

      <section style={{ ...card, display: 'grid', gap: 14 }}>
        <label>
          <p style={eyebrow}>Name</p>
          <input data-testid="proj-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Alfred — Personal Assistant" style={inp} />
        </label>
        {!isEdit && (
          <label>
            <p style={eyebrow}>Stage</p>
            <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
              {STAGES.map((s) => (
                <button key={s} data-testid={`proj-composer-stage-${s}`} onClick={() => setStage(s)} type="button"
                  style={{ padding: '5px 10px', borderRadius: 999, border: `1px solid ${stage === s ? STAGE_C[s] : C.hair}`, background: stage === s ? `${STAGE_C[s]}22` : 'transparent', color: stage === s ? STAGE_C[s] : C.icy, fontFamily: 'Inter', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{STAGE_LABEL[s]}</button>
              ))}
            </div>
          </label>
        )}
        <label>
          <p style={eyebrow}>Client (optional — leave blank for internal)</p>
          <select data-testid="proj-client-select" value={clientRef} onChange={(e) => setClientRef(e.target.value)} style={{ ...inp, appearance: 'none' }}>
            <option value="">— Internal / TravelTrucks —</option>
            {(customers || []).map((c) => (
              <option key={c.lead_id} value={c.lead_id}>{c.business_name || c.first_name || c.email || c.lead_id}</option>
            ))}
          </select>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label>
            <p style={eyebrow}>$ value (informational)</p>
            <input data-testid="proj-value" type="number" min="0" step="1" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 12000" style={inp} />
          </label>
          <label>
            <p style={eyebrow}>$ spend so far (manual, honest)</p>
            <input data-testid="proj-spend" type="number" min="0" step="1" value={manualSpend} onChange={(e) => setManualSpend(e.target.value)} placeholder="what you've spent on tokens/APIs" style={inp} />
          </label>
        </div>
        <label>
          <p style={eyebrow}>Notes</p>
          <textarea data-testid="proj-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="context, decisions, links to think about later…" style={{ ...inp, minHeight: 120, resize: 'vertical' }} />
        </label>
      </section>

      <section style={{ ...card }}>
        <p style={eyebrow}>◈ Emergent build links</p>
        <p style={{ margin: '0 0 12px', fontFamily: 'Inter', fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
          Paste the Emergent job URL (or full <code>?job_id=…</code>) with a label. Live status isn&apos;t available — Emergent doesn&apos;t expose a public jobs API. This just gets you back to the build in one tap.
        </p>
        <LinkList kind="emergent" links={eLinks} onAdd={(u, l) => addLink('emergent', u, l)} onRm={(id) => rmLink('emergent', id)} />
      </section>

      <section style={{ ...card }}>
        <p style={eyebrow}>⧉ Claude thread links</p>
        <p style={{ margin: '0 0 12px', fontFamily: 'Inter', fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
          Paste the Claude conversation URL with a label. No auto-read yet — just a bookmark.
        </p>
        <LinkList kind="claude" links={cLinks} onAdd={(u, l) => addLink('claude', u, l)} onRm={(id) => rmLink('claude', id)} />
      </section>

      {isEdit && (
        <section data-testid="proj-cost-estimator" style={{ ...card, borderStyle: 'dashed', borderColor: 'rgba(240,195,109,0.35)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <p style={{ ...eyebrow, margin: 0, color: '#F0C36D' }}>◇ AI cost estimator</p>
            <span style={{ fontFamily: 'Inter', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint, fontWeight: 700 }}>waiting on emergent api</span>
          </div>
          <p style={{ margin: '10px 0 0', fontFamily: 'Inter', fontSize: 11.5, color: C.muted, lineHeight: 1.55 }}>
            Live token/credit usage per Emergent job isn&apos;t available yet — the platform&apos;s public
            LLM key doesn&apos;t scope to the jobs API. When Emergent exposes an <code style={{ fontFamily: 'Menlo, monospace', color: '#F0C36D' }}>EMERGENT_JOBS_API_KEY</code>{' '}
            (or a similar token), this panel will show real-time status, credits burned and last-agent-message per linked job.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 14 }}>
            <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)' }}>
              <p style={{ ...eyebrow, margin: 0, fontSize: 9 }}>Manual spend logged</p>
              <p style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 18, fontWeight: 800, color: C.icy }}>
                {project?.manual_spend != null ? `$${Number(project.manual_spend).toLocaleString()}` : '—'}
              </p>
              <p style={{ margin: '2px 0 0', fontFamily: 'Inter', fontSize: 10, color: C.faint }}>edit above ↑</p>
            </div>
            <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)' }}>
              <p style={{ ...eyebrow, margin: 0, fontSize: 9 }}>Emergent jobs linked</p>
              <p style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 18, fontWeight: 800, color: '#34D1B0' }}>{(eLinks || []).length}</p>
              <p style={{ margin: '2px 0 0', fontFamily: 'Inter', fontSize: 10, color: C.faint }}>live status: unknown</p>
            </div>
            <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.03)' }}>
              <p style={{ ...eyebrow, margin: 0, fontSize: 9 }}>Estimated tokens</p>
              <p style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 18, fontWeight: 800, color: C.faint }}>—</p>
              <p style={{ margin: '2px 0 0', fontFamily: 'Inter', fontSize: 10, color: C.faint }}>needs API</p>
            </div>
          </div>
          {(eLinks || []).length > 0 && (
            <div style={{ marginTop: 14 }}>
              <p style={{ ...eyebrow, margin: 0, fontSize: 9 }}>Linked Emergent jobs — click to open</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                {(eLinks || []).map((l) => (
                  <div key={l.link_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 8, background: 'rgba(52,209,176,0.06)' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.faint, flexShrink: 0 }} title="live status unknown" />
                    <a href={l.url} target="_blank" rel="noreferrer" data-testid={`cost-est-job-${l.link_id}`}
                      style={{ flex: 1, minWidth: 0, fontFamily: 'Inter', fontSize: 12, color: '#34D1B0', textDecoration: 'none', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {l.label || 'Emergent build'}
                    </a>
                    <span style={{ fontFamily: 'Menlo, monospace', fontSize: 10, color: C.faint }}>waiting on API</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button data-testid="proj-cancel" onClick={onBack} style={ghost}>Cancel</button>
        <button data-testid="proj-save" onClick={save} disabled={busy} style={btnPrimary}>{busy ? '…' : (isEdit ? 'Update →' : 'Create project →')}</button>
      </div>
    </div>
  );
};

const LinkList = ({ kind, links, onAdd, onRm }) => {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const add = () => {
    if (!url.trim()) return;
    onAdd(url.trim(), label.trim());
    setUrl(''); setLabel('');
  };
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {(links || []).map((l) => (
        <div key={l.link_id} data-testid={`link-${kind}-${l.link_id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '9px 12px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.hair}`, borderRadius: 10, flexWrap: 'wrap' }}>
          <span style={{ color: kind === 'emergent' ? '#34D1B0' : '#F0C36D', fontFamily: 'Inter', fontSize: 12, fontWeight: 700 }}>{l.label || (kind === 'emergent' ? 'Emergent build' : 'Claude thread')}</span>
          <span style={{ flex: 1, fontFamily: 'Menlo, monospace', fontSize: 10.5, color: C.muted, wordBreak: 'break-all', minWidth: 0 }}>{l.url}</span>
          <a data-testid={`link-open-${l.link_id}`} href={l.url} target="_blank" rel="noreferrer" style={{ ...ghost, textDecoration: 'none', padding: '4px 10px', fontSize: 11, color: kind === 'emergent' ? '#34D1B0' : '#F0C36D', borderColor: kind === 'emergent' ? 'rgba(52,209,176,0.4)' : 'rgba(240,195,109,0.4)' }}>↗ Open</a>
          <button data-testid={`link-rm-${l.link_id}`} onClick={() => onRm(l.link_id)} style={{ ...ghost, padding: '4px 8px', fontSize: 11, color: '#FF8FC1', borderColor: 'rgba(235,37,141,0.4)' }}>✕</button>
        </div>
      ))}
      <div style={{ display: 'grid', gap: 6 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input data-testid={`link-${kind}-url`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder={kind === 'emergent' ? 'https://app.emergent.sh/?job_id=…' : 'https://claude.ai/chat/…'} style={{ ...inp, flex: 2, minWidth: 240 }} />
          <input data-testid={`link-${kind}-label`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="label (e.g. 'main build')" style={{ ...inp, flex: 1, minWidth: 160 }} />
          <button data-testid={`link-${kind}-add`} onClick={add} style={{ ...ghost, background: kind === 'emergent' ? 'rgba(52,209,176,0.08)' : 'rgba(240,195,109,0.08)' }}>+ Add</button>
        </div>
      </div>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────
const V2ProjectsPage = () => {
  const [view, setView] = useState('list'); // list | composer | detail
  const [projects, setProjects] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [toast, setToast] = useState(null);
  const say = (m) => { setToast(m); setTimeout(() => setToast(null), 3200); };

  const load = useCallback(async () => {
    try {
      const d = await req('GET', `/api/admin/projects?archived=${showArchived ? 'true' : 'false'}`);
      setProjects(d.projects || []);
    } catch (e) { setProjects([]); say('Load failed: ' + (e.message || 'unknown')); }
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    req('GET', '/api/admin/cq/customers').then((d) => setCustomers(d.customers || [])).catch(() => setCustomers([]));
  }, []);

  const openDetail = async (project_id) => {
    try { const p = await req('GET', `/api/admin/projects/${project_id}`); setEditing(p); setView('detail'); }
    catch (e) { say('Open failed'); }
  };
  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? Restorable from the Archive drawer.`)) return;
    try { await req('POST', `/api/admin/projects/${p.project_id}/archive`); say('🗄 Archived'); load(); }
    catch (e) { say('Archive failed'); }
  };
  const restore = async (p) => {
    try { await req('POST', `/api/admin/projects/${p.project_id}/restore`); say('↺ Restored'); load(); }
    catch (e) { say('Restore failed'); }
  };
  const destroy = async (p) => {
    if (!window.confirm(`Delete "${p.name}" FOREVER? This cannot be undone.`)) return;
    try { await req('DELETE', `/api/admin/projects/${p.project_id}?confirm=DELETE`); say('🗑 Deleted forever'); load(); }
    catch (e) { say('Delete failed: ' + (e.message || 'unknown')); }
  };

  return (
    <Chrome active="projects">
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px 64px' }}>
        {view === 'list' && (
          <ProjectList projects={projects} showArchived={showArchived} setShowArchived={setShowArchived}
            onOpen={openDetail} onNew={() => { setEditing(null); setView('composer'); }}
            onArchive={archive} onRestore={restore} onDelete={destroy} />
        )}
        {(view === 'composer' || view === 'detail') && (
          <Composer project={view === 'detail' ? editing : null} customers={customers} say={say}
            onBack={() => { setEditing(null); setView('list'); load(); }}
            onSaved={(p) => { setEditing(p); setView('detail'); load(); }} />
        )}
      </div>
      {toast && (
        <div data-testid="proj-toast" style={{ position: 'fixed', bottom: 26, left: '50%', transform: 'translateX(-50%)', background: '#0E0730', border: `1px solid ${C.hairBright}`, borderRadius: 999, padding: '11px 22px', color: C.icy, fontFamily: 'Inter', fontSize: 12.5, fontWeight: 700, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 3000 }}>{toast}</div>
      )}
    </Chrome>
  );
};

export default V2ProjectsPage;
